import React, { useState, useEffect } from 'react';
import { User, Phone, Mail, ArrowRight, ShieldCheck, Info, AlertCircle } from 'lucide-react';
import { AppUser } from '../../types';
import { syncUserProfileToFirestore } from '../../backend/authService';
import { clearProfilePromptSkip } from '../../utils/profilePrompt';

interface CompleteProfileFormProps {
  user: AppUser;
  onComplete: (updatedUser: AppUser) => void;
  onSkip: () => void;
  isMandatory?: boolean;
}

export const CompleteProfileForm: React.FC<CompleteProfileFormProps> = ({
  user,
  onComplete,
  onSkip,
  isMandatory = false
}) => {
  const [formData, setFormData] = useState({
    name: user.displayName || '',
    phone: user.phoneNumber || '',
    email: user.email || '',
    age: '',
    gender: '',
    category: '',
    country: 'India',
    state: '',
    city: '',
    pincode: ''
  });

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [isValid, setIsValid] = useState(false);
  const [loading, setLoading] = useState(false);

  const validateField = (name: string, value: string) => {
    if (name === 'category') {
      return true;
    }

    // Rule: No empty/blank, no pure whitespace, no special-character-only
    const cleanValue = value.trim();
    if (!cleanValue) return false;

    // Reject symbols-only strings
    const hasLettersOrNumbers = /[a-zA-Z0-9]/.test(cleanValue);
    if (!hasLettersOrNumbers) return false;

    if (name === 'email') {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanValue);
    }
    if (name === 'phone') {
        const digits = cleanValue.replace(/\D/g, '');
        return digits.length >= 8 && digits.length <= 15;
    }
    if (name === 'age') {
        const ageNum = Number(cleanValue);
        return Number.isInteger(ageNum) && ageNum >= 13 && ageNum <= 100;
    }
    if (name === 'gender') {
        return ['male', 'female', 'other'].includes(cleanValue);
    }
    if (name === 'pincode') {
        return /^\d{6}$/.test(cleanValue);
    }

    return true;
  };

  useEffect(() => {
    const newErrors: Record<string, boolean> = {};
    let allValid = true;

    (Object.entries(formData) as [string, string][]).forEach(([name, value]) => {
      const isFieldValid = validateField(name, value);
      if (!isFieldValid) {
        newErrors[name] = true;
        allValid = false;
      }
    });

    setErrors(newErrors);
    setIsValid(allValid);
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setLoading(true);
    const updatedUser: AppUser = {
      ...user,
      displayName: formData.name.trim(),
      phone: formData.phone.trim(),
      phoneNumber: formData.phone.trim(),
      email: formData.email.trim(),
      age: parseInt(formData.age, 10),
      gender: formData.gender,
      category: formData.category || null,
      country: formData.country,
      state: formData.state,
      city: formData.city,
      pincode: formData.pincode,
      profileComplete: true,
      updatedAt: new Date().toISOString()
    };

    try {
      await syncUserProfileToFirestore(updatedUser, true);
      try {
        await clearProfilePromptSkip(user.uid);
      } catch (error) {
        console.error('Profile was saved, but the skipped prompt marker could not be cleared:', error);
      }
      onComplete(updatedUser);
    } catch (error) {
      console.error('Could not save the completed profile:', error);
      alert(error instanceof Error ? error.message : 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950 flex flex-col items-center justify-center p-4 sm:p-6 overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-xl w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-blue-900 to-indigo-900 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-xl backdrop-blur-md">
              <ShieldCheck className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{isMandatory ? 'Complete Your Profile' : 'Fill Your Basic Mandatory Detail'}</h2>
              <p className="text-blue-200 text-xs mt-1">
                {isMandatory
                  ? 'Please provide accurate details to access all features on YeTryKar.'
                  : 'Recommended: complete your profile for a better experience. You can skip this for now.'}
              </p>
            </div>
          </div>
        </div>

        {/* Form Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 no-scrollbar">
          <form id="profile-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Full Name */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Full Name</label>
              <div className={`relative transition-all ${errors.name ? 'ring-2 ring-rose-500' : 'focus-within:ring-2 focus-within:ring-blue-900'}`}>
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Ajay Tripathi"
                  className="w-full pl-9 pr-4 py-3 bg-slate-50 rounded-xl text-sm focus:outline-none"
                />
              </div>
            </div>

            {/* Phone Number */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Phone Number</label>
              <div className={`relative transition-all ${errors.phone ? 'ring-2 ring-rose-500' : 'focus-within:ring-2 focus-within:ring-blue-900'}`}>
                <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="e.g. 9876543210"
                  className="w-full pl-9 pr-4 py-3 bg-slate-50 rounded-xl text-sm focus:outline-none"
                />
              </div>
            </div>

            {/* Email (Read Only - Protected Account Identity) */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  value={formData.email}
                  readOnly
                  className="w-full pl-9 pr-4 py-3 bg-slate-100 rounded-xl text-sm focus:outline-none text-slate-500 cursor-not-allowed"
                />
              </div>
            </div>

            {/* Age */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Age (13-100)</label>
              <div className={`relative transition-all ${errors.age ? 'ring-2 ring-rose-500' : 'focus-within:ring-2 focus-within:ring-blue-900'}`}>
                <Info className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="number"
                  value={formData.age}
                  onChange={e => setFormData({ ...formData, age: e.target.value })}
                  placeholder="e.g. 24"
                  className="w-full pl-9 pr-4 py-3 bg-slate-50 rounded-xl text-sm focus:outline-none"
                />
              </div>
            </div>

            {/* Gender Dropdown */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Gender</label>
              <select
                value={formData.gender}
                onChange={e => setFormData({ ...formData, gender: e.target.value })}
                className={`w-full px-4 py-3 bg-slate-50 rounded-xl text-sm focus:outline-none appearance-none transition-all ${errors.gender ? 'ring-2 ring-rose-500' : 'focus:ring-2 focus:ring-blue-900'}`}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other / Rather not say</option>
              </select>
            </div>

            {/* Category Dropdown (replaces Caste) */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Category (Optional)</label>
              <select
                value={formData.category}
                onChange={e => setFormData({ ...formData, category: e.target.value })}
                className={`w-full px-4 py-3 bg-slate-50 rounded-xl text-sm focus:outline-none appearance-none transition-all ${errors.category ? 'ring-2 ring-rose-500' : 'focus:ring-2 focus:ring-blue-900'}`}
              >
                <option value="">Select Category</option>
                <option value="General">General</option>
                <option value="OBC">OBC</option>
                <option value="SC">SC</option>
                <option value="ST">ST</option>
                <option value="Other">Other</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            </div>

            {/* Country */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Country</label>
              <select
                value={formData.country}
                onChange={e => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 appearance-none"
              >
                <option value="India">India</option>
                {/* Add more as needed */}
              </select>
            </div>

            {/* State */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">State</label>
              <input
                type="text"
                value={formData.state}
                onChange={e => setFormData({ ...formData, state: e.target.value })}
                placeholder="e.g. Uttar Pradesh"
                className={`w-full px-4 py-3 bg-slate-50 rounded-xl text-sm focus:outline-none transition-all ${errors.state ? 'ring-2 ring-rose-500' : 'focus:ring-2 focus:ring-blue-900'}`}
              />
            </div>

            {/* City */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">City</label>
              <input
                type="text"
                value={formData.city}
                onChange={e => setFormData({ ...formData, city: e.target.value })}
                placeholder="e.g. Lucknow"
                className={`w-full px-4 py-3 bg-slate-50 rounded-xl text-sm focus:outline-none transition-all ${errors.city ? 'ring-2 ring-rose-500' : 'focus:ring-2 focus:ring-blue-900'}`}
              />
            </div>

            {/* Pincode */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Pincode</label>
              <input
                type="text"
                maxLength={6}
                value={formData.pincode}
                onChange={e => setFormData({ ...formData, pincode: e.target.value.replace(/\D/g, '') })}
                placeholder="6-digit code"
                className={`w-full px-4 py-3 bg-slate-50 rounded-xl text-sm focus:outline-none transition-all ${errors.pincode ? 'ring-2 ring-rose-500' : 'focus:ring-2 focus:ring-blue-900'}`}
              />
            </div>
          </form>

          {/* Validation Help */}
          {Object.keys(errors).length > 0 && (
            <div className="p-3 bg-rose-50 rounded-2xl flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
              <p className="text-[11px] text-rose-700 leading-tight">
                Please complete all required fields correctly. Avoid blank values and symbols only. Age must be between 13-100 and pincode must contain 6 digits.
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-slate-100 flex items-center gap-4 bg-slate-50/50">
          {!isMandatory && (
            <button
              type="button"
              onClick={onSkip}
              className="flex-1 py-3 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
            >
              Skip for now
            </button>
          )}
          <button
            form="profile-form"
            type="submit"
            disabled={!isValid || loading}
            className="flex-[2] py-3.5 bg-blue-900 hover:bg-blue-950 disabled:bg-slate-300 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20 transition-all active:scale-95"
          >
            {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
                <>
                    <span>Submit & Continue</span>
                    <ArrowRight className="w-4 h-4" />
                </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
